import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import MapLocationDisplay from './MapLocationDisplay';
import WeatherDisplay from './WeatherDisplay';
import ListSelectionIndicator from './ListSelectionIndicator';
import { scrollMemoryService } from '../services/scrollMemoryService';

const DEFAULT_HEIGHT = 336;
const HOVER_HEIGHT = 560;
const TASK_HEIGHT = 28;
const HEADER_HEIGHT = 120;
const ACTION_MENU_HEIGHT = 48;
const SCROLL_AREA_PADDING = 20;

const ListTileCard = ({
  list,
  isHovered = false,
  onMouseEnter,
  onMouseLeave,
  isSelected = false,
  onToggleSelection,
  weather,
  actionLoading = {},
  onCompleteAll,
  onIncompleteAll,
  onDeleteList,
  onShareList,
  onExportList,
  onTaskToggle,
  onEditList,
  googleMapsApiKey,
}) => {
  const tasks = list?.tasks || [];

  // Determine available space
  const defaultTasksArea = DEFAULT_HEIGHT - HEADER_HEIGHT - (isHovered ? ACTION_MENU_HEIGHT : 0) - SCROLL_AREA_PADDING;
  const hoverTasksArea = HOVER_HEIGHT - HEADER_HEIGHT - ACTION_MENU_HEIGHT - SCROLL_AREA_PADDING;
  const defaultMaxTasks = Math.floor(defaultTasksArea / TASK_HEIGHT);
  const hoverMaxTasks = Math.floor(hoverTasksArea / TASK_HEIGHT);

  const needsExpansion = scrollMemoryService.needsExpansion(tasks.length, defaultTasksArea, TASK_HEIGHT);

  // Compute tile height
  const tileHeight = useMemo(() => {
    if (isHovered && needsExpansion) {
      const maxExpandedTasks = Math.min(tasks.length, hoverMaxTasks);
      const expandedHeight = HEADER_HEIGHT + (maxExpandedTasks * TASK_HEIGHT) + ACTION_MENU_HEIGHT + SCROLL_AREA_PADDING;
      return Math.min(Math.max(HOVER_HEIGHT, expandedHeight), typeof window !== 'undefined' ? window.innerHeight * 0.9 : HOVER_HEIGHT);
    }
    return DEFAULT_HEIGHT;
  }, [isHovered, needsExpansion, tasks.length, hoverMaxTasks]);

  const [scrollTop, setScrollTop] = useState(0);

  // Determine visible tasks using scroll memory
  const listMemory = scrollMemoryService.getListScrollPosition(list.id);

  const { displayTasks, hasMoreTasks, visibleRange } = useMemo(() => {
    if (isHovered && needsExpansion) {
      const maxToShow = Math.min(tasks.length, hoverMaxTasks);
      return {
        displayTasks: tasks.slice(0, maxToShow),
        hasMoreTasks: tasks.length > maxToShow,
        visibleRange: { start: 0, end: maxToShow },
      };
    }
    if (listMemory) {
      const range = scrollMemoryService.calculateVisibleTaskRange(
        listMemory.scrollTop || 0,
        TASK_HEIGHT,
        defaultTasksArea,
        tasks.length,
        defaultMaxTasks
      );
      return {
        displayTasks: tasks.slice(range.start, range.end),
        hasMoreTasks: tasks.length > range.end || range.start > 0,
        visibleRange: range,
      };
    }
    const maxToShow = Math.min(tasks.length, defaultMaxTasks);
    return {
      displayTasks: tasks.slice(0, maxToShow),
      hasMoreTasks: tasks.length > maxToShow,
      visibleRange: { start: 0, end: maxToShow },
    };
  }, [isHovered, needsExpansion, tasks, hoverMaxTasks, listMemory, defaultTasksArea, defaultMaxTasks]);

  const handleMouseLeave = () => {
    if (isHovered && needsExpansion) {
      const range = scrollMemoryService.calculateVisibleTaskRange(
        scrollTop,
        TASK_HEIGHT,
        defaultTasksArea,
        tasks.length,
        defaultMaxTasks
      );
      scrollMemoryService.setListScrollPosition(list.id, {
        scrollTop,
        visibleRange: range,
        taskCount: tasks.length,
      });
    }
    onMouseLeave?.();
  };

  return (
    <Card 
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        height: `${tileHeight}px`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'height 0.3s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': { elevation: 4, transform: 'translateY(-2px)' }
      }}
    >
      <ListSelectionIndicator
        selected={isSelected}
        visible={isHovered}
        onToggleSelection={onToggleSelection}
      />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', pb: isHovered ? 6 : 2, height: '100%' }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            gutterBottom 
            onClick={() => onEditList?.(list)}
            sx={{ fontWeight: 'bold', mb: 1, wordWrap: 'break-word', cursor: onEditList ? 'pointer' : 'default', '&:hover': { color: onEditList ? 'primary.main' : 'inherit' } }}
          >
            {list.title}
          </Typography>

          {(list.location_data && (list.address || list.location_data.coordinates)) && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, color: 'text.secondary' }}>
              <Box onClick={() => onEditList?.(list)} sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, cursor: onEditList ? 'pointer' : 'default', '&:hover': { color: onEditList ? 'primary.main' : 'inherit' } }}>
                <LocationOn sx={{ fontSize: 16, mr: 0.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {list.address || `${list.location_data.coordinates.lat.toFixed(6)}, ${list.location_data.coordinates.lng.toFixed(6)}`}
                </Typography>
              </Box>
              {weather && (
                <Box sx={{ ml: 1, flexShrink: 0 }}>
                  <WeatherDisplay weather={weather} showDetails={true} size="small" />
                </Box>
              )}
            </Box>
          )}

          {list.location_data && (
            <Box sx={{ mb: 2, '& > div': { pointerEvents: 'auto' } }}>
              <MapLocationDisplay
                location={list.location_data}
                draggable={false}
                width="100%"
                height="120px"
                zoom={13}
                apiKey={googleMapsApiKey}
                showWhenEmpty={false}
              />
            </Box>
          )}
        </Box>

        <Box 
          onScroll={isHovered ? (e) => setScrollTop(e.target.scrollTop) : undefined}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            maxHeight: `calc(${tileHeight}px - 200px)`,
            pr: 1,
            cursor: onEditList ? 'pointer' : 'default',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '2px' },
          }}
          onClick={() => onEditList?.(list)}
        >
          {displayTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No tasks yet</Typography>
          ) : (
            displayTasks.map((task, index) => (
              <Box key={task.id || index} onClick={(e) => {
                if (e.target.closest('input[type="checkbox"]') || e.target.closest('.MuiCheckbox-root')) { e.stopPropagation(); }
              }} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, minHeight: '20px' }}>
                <Checkbox size="small" checked={task.completed || false} onChange={() => onTaskToggle?.(list.id, task.id, task.completed)} onClick={(e) => e.stopPropagation()} sx={{ p: 0, mr: 1, mt: 0.25, '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                <Typography variant="body2" sx={{ textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'text.secondary' : 'text.primary', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto', lineHeight: 1.2 }}>
                  {task.title}
                </Typography>
              </Box>
            ))
          )}

          {hasMoreTasks && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
              {visibleRange.start > 0 && `... ${visibleRange.start} tasks above, `}
              {visibleRange.end < tasks.length && `${tasks.length - visibleRange.end} more tasks${visibleRange.start > 0 ? ' below' : ''}`}
            </Typography>
          )}
        </Box>
      </CardContent>

      {/* Action Menu */}
      <Fade in={isHovered}>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(8px)', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, gap: 2 }}>
          <Tooltip title="Check all tasks">
            <IconButton size="small" onClick={() => onCompleteAll?.(list.id)} disabled={actionLoading[`complete-${list.id}`]} sx={{ fontSize: '16px' }}>
              {actionLoading[`complete-${list.id}`] ? (<CircularProgress size={16} />) : ('☑️')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Uncheck all tasks">
            <IconButton size="small" onClick={() => onIncompleteAll?.(list.id)} disabled={actionLoading[`incomplete-${list.id}`]} sx={{ fontSize: '16px' }}>
              {actionLoading[`incomplete-${list.id}`] ? (<CircularProgress size={16} />) : ('☐')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Share list">
            <IconButton size="small" onClick={(e) => onShareList?.(e, list.id)} sx={{ fontSize: '16px' }}>
              📤
            </IconButton>
          </Tooltip>
          <Tooltip title="Export as JSON">
            <IconButton size="small" onClick={(e) => onExportList?.(e, list.id)} disabled={actionLoading[`export-${list.id}`]} sx={{ fontSize: '16px' }}>
              {actionLoading[`export-${list.id}`] ? (<CircularProgress size={16} />) : ('💾')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete list">
            <IconButton size="small" onClick={() => onDeleteList?.(list.id)} disabled={actionLoading[`delete-${list.id}`]} sx={{ fontSize: '16px', color: 'error.main' }}>
              {actionLoading[`delete-${list.id}`] ? (<CircularProgress size={16} />) : ('🗑️')}
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>
    </Card>
  );
};

export default ListTileCard;

